import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { DemoFan } from '../../datas/demoFan';
import {
  buildCompanionSendMessage,
  MUSIC_GENERATION_COMPANION_MESSAGES,
} from '../../features/AvatarSession/constants/musicGenerationAvatarMessages';
import { GenerationResponse } from '../../services/generationsApi';
import { OidcUser } from '../../types/oidcUser';
import { analytics } from '../../utils/analytics';
import { getOrganizationId } from '../../utils/getOrganization';
import { useStationMode } from '../station';

interface UseAvatarMessagingParams {
  isDataChannelOpen: boolean;
  activeMusicGeneration?: GenerationResponse;
  song1Ready: boolean;
  song2Ready: boolean;
  userInput?: string;
  fanData?: DemoFan;
  designImage1: string | null;
  designImage2: string | null;
  sendMessageWithBridge: (message: any) => void;
  sendMessage: (message: any) => void;
  isPresentationMode?: boolean;
  isWebpageMode?: boolean;
  isBusinessPlanMode?: boolean;
  isRegeneratingLyrics?: boolean;
  isRegeneratingMusic?: boolean;
}

export const useAvatarMessaging = ({
  isDataChannelOpen,
  activeMusicGeneration,
  song1Ready,
  song2Ready,
  userInput,
  fanData,
  designImage1,
  designImage2,
  sendMessageWithBridge,
  sendMessage,
  isPresentationMode = false,
  isWebpageMode = false,
  isBusinessPlanMode = false,
  isRegeneratingLyrics = false,
  isRegeneratingMusic = false,
}: UseAvatarMessagingParams) => {
  const { user } = useAuth();
  const isStationMode = useStationMode();
  const hasSentSong1Message = useRef(false);
  const hasSentSong2Message = useRef(false);
  const hasSentCompletionMessage = useRef(false);
  const hasSentSongGeneratedMessage = useRef(false);
  const hasSentDesignerInitialMessage = useRef(false);
  const hasSentDesignerCompletionMessage = useRef(false);
  const hasSentPresentationInitialMessage = useRef(false);
  const hasSentWebpageInitialMessage = useRef(false);
  const hasSentBusinessPlanInitialMessage = useRef(false);
  const hasLoggedGenerationComplete = useRef(false);

  const hasSentLyricsReadyState = useRef(false);
  const hasSentCoverGeneratingState = useRef(false);
  const hasSentCoverReadyState = useRef(false);
  const hasSentSongGeneratingState = useRef(false);

  const prevRegeneratingLyricsRef = useRef(isRegeneratingLyrics);
  const prevRegeneratingMusicRef = useRef(isRegeneratingMusic);

  const shouldSendMessage = isDataChannelOpen || isStationMode;
  const companionReady = isDataChannelOpen || isStationMode;

  const hasLyrics = !!activeMusicGeneration?.lyrics;
  const hasCover =
    !!activeMusicGeneration?.thumbnailUrl1 ||
    !!activeMusicGeneration?.thumbnailUrl2;

  // Lyrics ready: companion session (data channel or station bridge) must be up
  useEffect(() => {
    if (
      !companionReady ||
      !activeMusicGeneration ||
      !hasLyrics ||
      hasSentLyricsReadyState.current
    ) {
      return;
    }

    sendMessageWithBridge(
      buildCompanionSendMessage(
        MUSIC_GENERATION_COMPANION_MESSAGES.lyricsReady,
        true,
        false,
        isStationMode ? { state: 'lyrics_ready' } : undefined,
      ),
    );
    hasSentLyricsReadyState.current = true;
  }, [
    companionReady,
    isStationMode,
    activeMusicGeneration,
    hasLyrics,
    sendMessageWithBridge,
  ]);

  useEffect(() => {
    const prev = prevRegeneratingLyricsRef.current;
    if (
      companionReady &&
      activeMusicGeneration &&
      prev &&
      !isRegeneratingLyrics &&
      hasLyrics
    ) {
      sendMessageWithBridge(
        buildCompanionSendMessage(
          MUSIC_GENERATION_COMPANION_MESSAGES.lyricsRegenerated,
          true,
          false,
        ),
      );
    }
    prevRegeneratingLyricsRef.current = isRegeneratingLyrics;
  }, [
    companionReady,
    activeMusicGeneration,
    isRegeneratingLyrics,
    hasLyrics,
    sendMessageWithBridge,
  ]);

  useEffect(() => {
    const prev = prevRegeneratingMusicRef.current;
    if (
      companionReady &&
      activeMusicGeneration &&
      !prev &&
      isRegeneratingMusic
    ) {
      sendMessageWithBridge(
        buildCompanionSendMessage(
          MUSIC_GENERATION_COMPANION_MESSAGES.songsGenerating,
          true,
          false,
        ),
      );
    }
    prevRegeneratingMusicRef.current = isRegeneratingMusic;
  }, [
    companionReady,
    activeMusicGeneration,
    isRegeneratingMusic,
    sendMessageWithBridge,
  ]);

  // Send cover_generating state when lyrics are ready but covers are not
  useEffect(() => {
    if (
      isStationMode &&
      activeMusicGeneration &&
      hasLyrics &&
      !hasCover &&
      !hasSentCoverGeneratingState.current
    ) {
      hasSentCoverGeneratingState.current = true;
    }
  }, [
    isStationMode,
    activeMusicGeneration,
    hasLyrics,
    hasCover,
    sendMessageWithBridge,
  ]);

  // Send cover_ready state when cover becomes available
  useEffect(() => {
    if (
      isStationMode &&
      activeMusicGeneration &&
      hasCover &&
      !hasSentCoverReadyState.current
    ) {
      hasSentCoverReadyState.current = true;
    }
  }, [isStationMode, activeMusicGeneration, hasCover, sendMessageWithBridge]);

  useEffect(() => {
    if (
      isStationMode &&
      activeMusicGeneration &&
      hasCover &&
      !song1Ready &&
      !hasSentSongGeneratingState.current
    ) {
      hasSentSongGeneratingState.current = true;
    }
  }, [
    isStationMode,
    activeMusicGeneration,
    hasCover,
    song1Ready,
    sendMessageWithBridge,
  ]);

  useEffect(() => {
    if (
      activeMusicGeneration &&
      shouldSendMessage &&
      song1Ready &&
      !!activeMusicGeneration.songUrl1?.trim() &&
      !hasSentSong1Message.current
    ) {
      sendMessageWithBridge(
        buildCompanionSendMessage(
          MUSIC_GENERATION_COMPANION_MESSAGES.firstSongVariationReady,
          false,
          false,
        ),
      );
      hasSentSong1Message.current = true;
    }
  }, [
    shouldSendMessage,
    song1Ready,
    activeMusicGeneration,
    sendMessageWithBridge,
  ]);

  useEffect(() => {
    if (
      activeMusicGeneration &&
      shouldSendMessage &&
      song2Ready &&
      !!activeMusicGeneration.songUrl2?.trim() &&
      !hasSentSong2Message.current
    ) {
      sendMessageWithBridge(
        buildCompanionSendMessage(
          MUSIC_GENERATION_COMPANION_MESSAGES.secondSongVariationReady,
          false,
          false,
        ),
      );
      hasSentSong2Message.current = true;
    }
  }, [
    shouldSendMessage,
    song2Ready,
    activeMusicGeneration,
    sendMessageWithBridge,
  ]);

  useEffect(() => {
    const isSingleSong = !activeMusicGeneration?.conversionId2;
    // Require real URLs — song1Ready/song2Ready use sticky refs that stay true after regen on same taskId.
    const songUrl1Present = !!activeMusicGeneration?.songUrl1?.trim();
    if (
      !companionReady ||
      !activeMusicGeneration ||
      !isSingleSong ||
      !song1Ready ||
      !songUrl1Present ||
      hasSentCompletionMessage.current
    ) {
      return;
    }

    sendMessageWithBridge(
      buildCompanionSendMessage(
        MUSIC_GENERATION_COMPANION_MESSAGES.singleSongComplete,
        true,
        false,
      ),
    );
    hasSentCompletionMessage.current = true;

    if (!hasLoggedGenerationComplete.current) {
      analytics.logEvent('music_generation_complete');
      hasLoggedGenerationComplete.current = true;
    }
  }, [
    companionReady,
    song1Ready,
    activeMusicGeneration,
    sendMessageWithBridge,
  ]);

  useEffect(() => {
    const isSingleSong = !activeMusicGeneration?.conversionId2;
    const areBothSongsComplete = song1Ready && song2Ready;
    const bothUrlsPresent =
      !!activeMusicGeneration?.songUrl1?.trim() &&
      !!activeMusicGeneration?.songUrl2?.trim();

    if (
      !companionReady ||
      !activeMusicGeneration ||
      isSingleSong ||
      !areBothSongsComplete ||
      !bothUrlsPresent ||
      hasSentCompletionMessage.current
    ) {
      return;
    }

    sendMessageWithBridge(
      buildCompanionSendMessage(
        MUSIC_GENERATION_COMPANION_MESSAGES.bothSongsComplete,
        true,
        false,
      ),
    );
    hasSentCompletionMessage.current = true;

    if (!hasLoggedGenerationComplete.current) {
      analytics.logEvent('music_generation_complete');
      hasLoggedGenerationComplete.current = true;
    }
  }, [
    companionReady,
    song1Ready,
    song2Ready,
    activeMusicGeneration,
    sendMessageWithBridge,
  ]);

  useEffect(() => {
    const isSingleSong = !activeMusicGeneration?.conversionId2;
    const url1 = activeMusicGeneration?.songUrl1?.trim();
    const url2 = activeMusicGeneration?.songUrl2?.trim();
    const isSingleComplete = isSingleSong && !!url1;
    const areBothComplete = !isSingleSong && !!url1 && !!url2;
    const isComplete = isSingleComplete || areBothComplete;

    if (
      shouldSendMessage &&
      isComplete &&
      !hasSentSongGeneratedMessage.current
    ) {
      const userId = (user as OidcUser)?.profile?.sub || '';
      const organizationId = getOrganizationId(user as OidcUser);

      if (!isStationMode) {
        sendMessage({
          type: 'send_back_channel_message',
          data: {
            type: 'song_generated',
            data: {
              userId,
              organizationId,
            },
          },
        });
      }
      hasSentSongGeneratedMessage.current = true;
    }
  }, [
    shouldSendMessage,
    song1Ready,
    song2Ready,
    activeMusicGeneration,
    sendMessage,
    user,
    isStationMode,
  ]);

  useEffect(() => {
    if (
      userInput &&
      shouldSendMessage &&
      !hasSentDesignerInitialMessage.current &&
      !isPresentationMode &&
      !isWebpageMode &&
      !isBusinessPlanMode
    ) {
      if (!isStationMode) {
        const designerName =
          fanData?.given_name || fanData?.artist_alias || 'the designer';
        const message = `The user has requested ${designerName} to create designs. The prompt is: "${userInput}". The system is generating two distinct variations of the image on your behalf. If the user inquires about the image, inform them that it's currently being created. Once the images are ready, you will receive a notification. Meanwhile, continue keeping the conversation naturally engaging with the user.`;
        sendMessageWithBridge({
          type: 'send_message',
          data: {
            role: 'system',
            text: message,
            trigger_response: true,
            delay: true,
          },
        });
      }

      hasSentDesignerInitialMessage.current = true;
    }
  }, [
    shouldSendMessage,
    userInput,
    fanData,
    sendMessageWithBridge,
    isStationMode,
    isPresentationMode,
    isWebpageMode,
    isBusinessPlanMode,
  ]);

  useEffect(() => {
    if (
      userInput &&
      shouldSendMessage &&
      designImage1 &&
      designImage2 &&
      !hasSentDesignerCompletionMessage.current
    ) {
      const message = `Both images are now ready!`;
      if (!isStationMode) {
        sendMessageWithBridge({
          type: 'send_message',
          data: {
            role: 'system',
            text: message,
            trigger_response: true,
            delay: true,
          },
        });
      }
      hasSentDesignerCompletionMessage.current = true;
    }
  }, [
    shouldSendMessage,
    userInput,
    designImage1,
    designImage2,
    sendMessageWithBridge,
    isStationMode,
  ]);

  useEffect(() => {
    if (
      userInput &&
      shouldSendMessage &&
      isPresentationMode &&
      !hasSentPresentationInitialMessage.current
    ) {
      hasSentPresentationInitialMessage.current = true;
    }
  }, [
    shouldSendMessage,
    userInput,
    sendMessageWithBridge,
    isStationMode,
    isPresentationMode,
  ]);

  useEffect(() => {
    if (
      userInput &&
      shouldSendMessage &&
      isWebpageMode &&
      !hasSentWebpageInitialMessage.current
    ) {
      hasSentWebpageInitialMessage.current = true;
    }
  }, [
    shouldSendMessage,
    userInput,
    sendMessageWithBridge,
    isStationMode,
    isWebpageMode,
  ]);

  useEffect(() => {
    if (
      userInput &&
      shouldSendMessage &&
      isBusinessPlanMode &&
      !hasSentBusinessPlanInitialMessage.current
    ) {
      hasSentBusinessPlanInitialMessage.current = true;
    }
  }, [
    shouldSendMessage,
    userInput,
    sendMessageWithBridge,
    isStationMode,
    isBusinessPlanMode,
  ]);
};
